import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageDetailLayout } from '@/shared/layout';
import { useAssetDetail, useAssets, useAssetHealthLogs, useAssetImages } from './hooks/useAssets';
import { Button, Badge, Card, FeaturePlaceholder, Alert, KPICard } from '@/components/ui';
import { AssetFormModal } from './components/AssetFormModal';
import { AssetHealthLogModal } from './components/AssetHealthLogModal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { AuthButton } from '@/core/permissions/AuthButton';
import { useAuth } from '@/core/auth/AuthContext';
import { toast } from 'sonner';

const healthColors: Record<string, string> = {
  good: 'bg-emerald-100 text-emerald-600',
  warning: 'bg-amber-100 text-amber-600',
  bad: 'bg-red-100 text-red-600',
};

const healthLabels: Record<string, string> = {
  good: 'Khỏe mạnh',
  warning: 'Cần chú ý',
  bad: 'Vấn đề',
};

export default function AssetDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';

  const { data: asset, isLoading, error } = useAssetDetail(id);
  const { deleteAsset, isDeleting } = useAssets(companyId);
  const { data: healthLogs = [], createHealthLog, isCreatingLog } = useAssetHealthLogs(id);
  const { data: images = [], uploadImage, isUploading } = useAssetImages(id);

  const [activeTab, setActiveTab] = useState('overview');
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showHealthUpdate, setShowHealthUpdate] = useState(false);

  const handleDeleteConfirm = async () => {
    if (!asset) return;
    try {
      await deleteAsset(asset.id);
      toast.success('Đã xóa tài sản thành công');
      navigate('/maintenance/assets');
    } catch (error: any) {
      toast.error('Lỗi khi xóa: ' + error.message);
    }
  };

  const handleHealthSave = async (log: any) => {
    await createHealthLog(log);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      try {
        await uploadImage({ file: e.target.files[0], type: 'general' });
        toast.success('Đã tải lên hình ảnh');
      } catch (error: any) {
        toast.error('Lỗi: ' + error.message);
      }
    }
  };

  if (isLoading) return <div className="p-12 text-center text-slate-500 text-sm">Đang tải thông tin tài sản...</div>;
  if (!asset) return <div className="p-12 text-center text-red-500 font-bold">Không tìm thấy dữ liệu tài sản</div>;

  const tabs = [
    { value: 'overview', label: 'Tổng quan' },
    { value: 'maintenance', label: 'Bảo dưỡng' },
    { value: 'health', label: 'Sức khỏe cây' },
    { value: 'images', label: 'Hình ảnh' },
    { value: 'costs', label: 'Chi phí' },
    { value: 'logs', label: 'Nhật ký' },
  ];

  return (
    <PageDetailLayout
      title={asset.asset_name}
      description={
        <div className="flex items-center gap-3 mt-1">
          <Badge className={`${healthColors[asset.health_status] || 'bg-slate-100 text-slate-600'} border-transparent text-[10px] font-bold `}>
            <i className="ri-heart-pulse-line mr-1" />
            {healthLabels[asset.health_status] || asset.health_status}
          </Badge>
          <span className="text-sm text-slate-400 font-mono">{asset.asset_code}</span>
          <span className="text-slate-300">|</span>
          <span className="text-sm text-slate-500 flex items-center gap-1">
            <i className="ri-map-pin-2-line" />
            {asset.zone?.zone_name || 'N/A'}
          </span>
          <span className="text-slate-300">|</span>
          <span className="text-sm text-slate-500">{asset.asset_type_code}</span>
        </div>
      }
      breadcrumbs={[
        { label: 'Tài sản cảnh quan', path: '/maintenance/assets' },
        { label: asset.asset_code }
      ]}
      headerActions={
        <div className="flex gap-2">
          <AuthButton module="maintenance" action="update" variant="outline" icon="ri-edit-line" onClick={() => setShowEdit(true)}>
            Chỉnh sửa
          </AuthButton>
          <AuthButton
            module="maintenance"
            action="delete"
            variant="ghost"
            className="text-red-500 hover:bg-red-50"
            icon="ri-delete-bin-line"
            onClick={() => setShowDelete(true)}
          />
        </div>
      }
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {activeTab === 'overview' && (
            <>
              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <i className="ri-information-line text-os-primary" />
                  Thông số sinh học & Kỹ thuật
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-y-6 gap-x-4">
                  <div>
                    <p className="text-[10px]  text-slate-400 font-bold mb-1">Loài / Giống</p>
                    <p className="text-sm font-semibold text-slate-700 italic">{asset.species || 'Chưa cập nhật'}</p>
                  </div>
                  <div>
                    <p className="text-[10px]  text-slate-400 font-bold mb-1">Số lượng / Đơn vị</p>
                    <p className="text-sm font-bold text-slate-900">{asset.quantity} {asset.unit}</p>
                  </div>
                  <div>
                    <p className="text-[10px]  text-slate-400 font-bold mb-1">Diện tích bao phủ</p>
                    <p className="text-sm font-semibold text-slate-700">{asset.area_m2?.toLocaleString() || '-'} m²</p>
                  </div>
                  <div>
                    <p className="text-[10px]  text-slate-400 font-bold mb-1">Ngày trồng / Lắp đặt</p>
                    <p className="text-sm font-semibold text-slate-700">{asset.planting_date ? new Date(asset.planting_date).toLocaleDateString('vi-VN') : '---'}</p>
                  </div>
                  <div>
                    <p className="text-[10px]  text-slate-400 font-bold mb-1">Lần bảo dưỡng cuối</p>
                    <p className="text-sm font-semibold text-slate-700">{asset.last_maintenance_date ? new Date(asset.last_maintenance_date).toLocaleDateString('vi-VN') : 'Chưa có'}</p>
                  </div>
                  <div>
                    <p className="text-[10px]  text-slate-400 font-bold mb-1">Bảo dưỡng tiếp theo</p>
                    <p className="text-sm font-semibold text-os-primary">{asset.next_maintenance_date ? new Date(asset.next_maintenance_date).toLocaleDateString('vi-VN') : '---'}</p>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <i className="ri-text-snippet text-os-primary" />
                  Vị trí & Ghi chú
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {asset.location_note || 'Chưa có thông tin vị trí chi tiết.'}
                </p>
                {asset.gps_lat && asset.gps_lng && (
                  <div className="mt-4 p-3 bg-slate-50 rounded-xl flex items-center gap-3">
                    <i className="ri-gps-line text-os-primary" />
                    <span className="text-xs font-mono text-slate-500">Tọa độ: {asset.gps_lat}, {asset.gps_lng}</span>
                    <Button variant="ghost" size="sm" className="ml-auto text-[10px] h-7">Xem trên bản đồ</Button>
                  </div>
                )}
              </Card>
            </>
          )}

          {activeTab === 'health' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <KPICard title="Tình trạng hiện tại" value={healthLabels[asset.health_status]} icon="ri-heart-pulse-line" variant={asset.health_status === 'good' ? 'success' : asset.health_status === 'warning' ? 'warning' : 'danger'} />
                <KPICard title="Số lần kiểm tra" value={healthLogs.length} icon="ri-search-eye-line" variant="info" />
                <KPICard title="Ngày kiểm tra cuối" value={healthLogs[0] ? new Date(healthLogs[0].checked_at).toLocaleDateString('vi-VN') : 'N/A'} icon="ri-calendar-check-line" variant="info" />
              </div>

              <Card className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="font-bold text-slate-900">Lịch sử kiểm tra sức khỏe</h3>
                  <Button variant="primary" size="sm" icon="ri-add-line" onClick={() => setShowHealthUpdate(true)}>Thêm nhật ký</Button>
                </div>

                <div className="space-y-6">
                  {healthLogs.length > 0 ? healthLogs.map((log, idx) => (
                    <div key={log.id} className="relative pl-8 pb-6 border-l-2 border-slate-100 last:border-transparent last:pb-0">
                      <div className={`absolute left-[-9px] top-0 w-4 h-4 rounded-full border-2 border-white ${healthColors[log.health_status].split(' ')[0]}`} />
                      <div className="bg-white rounded-xl border border-slate-100 p-4 shadow-sm">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <Badge className={`${healthColors[log.health_status]} text-[10px]  font-bold`}>{healthLabels[log.health_status]}</Badge>
                            <p className="text-[10px] text-slate-400 mt-1">{new Date(log.checked_at).toLocaleString('vi-VN')}</p>
                          </div>
                          <p className="text-xs text-slate-500 font-semibold">Bởi: {log.checker?.full_name || 'Hệ thống'}</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <p className="text-[10px]  text-slate-400 font-bold mb-1">Vấn đề / Sâu bệnh</p>
                            <p className="text-sm text-slate-700">{log.disease_note || 'Không ghi nhận'}</p>
                          </div>
                          <div>
                            <p className="text-[10px]  text-slate-400 font-bold mb-1">Hành động xử lý</p>
                            <p className="text-sm text-slate-700">{log.action_taken || 'Không có'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-12">
                      <i className="ri-shield-check-line text-4xl text-slate-200 mb-2" />
                      <p className="text-slate-400 text-sm">Chưa có lịch sử kiểm tra sức khỏe</p>
                    </div>
                  )}
                </div>
              </Card>
            </div>
          )}

          {activeTab === 'images' && (
            <Card className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="font-bold text-slate-900">Thư viện hình ảnh</h3>
                <label className="cursor-pointer">
                  <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} disabled={isUploading} />
                  <Button variant="outline" size="sm" icon="ri-upload-2-line" as="span" isLoading={isUploading}>Tải ảnh lên</Button>
                </label>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {images.length > 0 ? images.map(img => (
                  <div key={img.id} className="group relative aspect-square rounded-xl overflow-hidden bg-slate-100 border border-slate-200">
                    <img src={img.image_url} alt={img.description} className="w-full h-full object-cover transition-transform group-hover:scale-110" />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <Button variant="ghost" size="sm" className="text-white hover:text-white" icon="ri-eye-line" />
                      <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-500" icon="ri-delete-bin-line" />
                    </div>
                  </div>
                )) : (
                  <div className="col-span-full text-center py-12">
                    <i className="ri-image-line text-4xl text-slate-200 mb-2" />
                    <p className="text-slate-400 text-sm">Chưa có hình ảnh nào</p>
                  </div>
                )}
              </div>
            </Card>
          )}

          {(activeTab === 'maintenance' || activeTab === 'costs' || activeTab === 'logs') && (
            <FeaturePlaceholder
              title={tabs.find(t => t.value === activeTab)?.label || 'Tính năng'}
              description="Phần này đang được kết nối với phân hệ Công việc & Tài chính để hiển thị dữ liệu thực tế."
            />
          )}
        </div>

        <div className="space-y-6">
          <Card className="p-6">
            <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
              <i className="ri-history-line text-slate-400" />
              Sức khỏe gần đây
            </h4>
            <div className="space-y-4">
              {healthLogs.length > 0 ? (
                healthLogs.slice(0, 3).map(log => (
                  <div key={log.id} className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                    <div className="flex justify-between items-center mb-1">
                      <Badge className={`${healthColors[log.health_status]} text-[9px]  border-transparent font-bold`}>
                        {healthLabels[log.health_status]}
                      </Badge>
                      <span className="text-[10px] text-slate-400">{new Date(log.checked_at).toLocaleDateString('vi-VN')}</span>
                    </div>
                    <p className="text-[11px] text-slate-600 line-clamp-2">{log.disease_note || 'Không có vấn đề'}</p>
                  </div>
                ))
              ) : (
                <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100 text-center">
                  <i className="ri-checkbox-circle-line text-emerald-500 text-2xl mb-1" />
                  <p className="text-xs font-bold text-emerald-900">Sức khỏe ổn định</p>
                  <p className="text-[10px] text-emerald-600">Chưa có ghi nhận sâu bệnh</p>
                </div>
              )}
              <div className="pt-4 border-t border-slate-50">
                <Button variant="outline" size="sm" icon="ri-add-line" className="w-full text-xs" onClick={() => setShowHealthUpdate(true)}>Cập nhật sức khỏe</Button>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <AssetFormModal
        isOpen={showEdit}
        asset={asset}
        companyId={companyId}
        onClose={() => setShowEdit(false)}
      />

      <AssetHealthLogModal
        isOpen={showHealthUpdate}
        assetId={asset.id}
        assetName={asset.asset_name}
        onSave={handleHealthSave}
        onClose={() => setShowHealthUpdate(false)}
      />

      <ConfirmDialog
        isOpen={showDelete}
        title="Xác nhận xóa tài sản"
        message={`Bạn có chắc chắn muốn xóa tài sản "${asset.asset_name}"? Mọi dữ liệu về lịch sử bảo dưỡng và sức khỏe sẽ bị xóa vĩnh viễn.`}
        confirmText="Xác nhận xóa"
        isDestructive={true}
        isLoading={isDeleting}
        onConfirm={handleDeleteConfirm}
        onClose={() => setShowDelete(false)}
      />
    </PageDetailLayout>
  );
}
